import { isVotingEnabled } from "@canucci/base/canvas";
import type { CanvasData } from "@/services/apitypes";
import { useCanvas } from "./useCanvas";

/**
 * Hook to get voting status for the current canvas.
 *
 * Returns whether voting is enabled and whether voting is currently closed.
 * Voting is considered closed when:
 * - Voting is enabled AND
 * - voting_closed_at is a valid datetime string
 *
 * Note: We check for a valid timestamp, NOT comparing it to current time.
 * This means clock skew between client/server is NOT an issue - we only care if
 * the manager has closed voting (indicated by the presence of the timestamp).
 *
 * @returns Object with canvas-level and participant-control voting state.
 */
export function getVotingStatus(voting: CanvasData["voting"] | undefined, isParticipation: boolean) {
  const canvasVotingEnabled = Boolean(isVotingEnabled(voting) && voting?.target_type === "subtheme");
  const participantVotingEnabled = Boolean(isParticipation && canvasVotingEnabled);
  const votingClosedAt = voting?.closed_at;
  const isVotingClosed =
    canvasVotingEnabled &&
    typeof votingClosedAt === "string" &&
    votingClosedAt.length > 0 &&
    !Number.isNaN(Date.parse(votingClosedAt));

  return {
    canvasVotingEnabled,
    participantVotingEnabled,
    votingEnabled: participantVotingEnabled,
    isVotingClosed,
  };
}

export function useVotingStatus() {
  const { canvasResult, isParticipation } = useCanvas();
  const canvasData = canvasResult?.data;

  return getVotingStatus(canvasData?.voting, isParticipation);
}
